import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import {
  provideAuth as provideOidc,
  withAppInitializerAuthCheck,
} from 'angular-auth-oidc-client';
import { OAuthConfig } from './models/auth';

export const provideAuth = (
  config: OAuthConfig,
  secureRoutes: string[],
): EnvironmentProviders =>
  makeEnvironmentProviders([
    provideOidc(
      {
        config: {
          authority: config.authority,
          authWellknownEndpointUrl: config.authWellknownEndpointUrl,
          clientId: config.clientId,
          scope: config.scope,
          responseType: config.responseType,
          maxIdTokenIatOffsetAllowedInSeconds:
            config.maxIdTokenIatOffsetAllowedInSeconds,
          redirectUrl: window.location.origin + '/callback',
          postLogoutRedirectUri: window.location.origin,
          silentRenew: true,
          useRefreshToken: true,
          // Azure Entra ID returns the refresh-flow id_token *with* a nonce and a
          // fresh auth_time, both of which angular-auth-oidc-client validates too
          // strictly by default. Without these flags the periodic silent renew
          // fails ("incorrect nonce" -> "token(s) validation failed, resetting"),
          // which wipes the stored tokens and 403s every subsequent request. Both
          // are the library's documented Azure work-arounds, and both are no-ops
          // for the Keycloak dev IdP (its refreshed id_tokens carry no nonce).
          ignoreNonceAfterRefresh: true,
          disableRefreshIdTokenAuthTimeValidation: true,
          // Access token audience is the m3-api (api://40003105-...), not Microsoft
          // Graph, so the Graph userinfo endpoint rejects it with 401. User data
          // comes from the backend (/api/employeeV2/account), not OIDC userinfo.
          autoUserInfo: false,
          renewUserInfoAfterTokenRenew: false,
          // After processing the /callback code, the library otherwise auto-
          // navigates to postLoginRoute ('/'), which hits our empty-path
          // redirectTo: 'dashboard' and re-runs authGuard for /dashboard —
          // clobbering the deep-link URL the guard saved in originalRoute before
          // AuthCallback can read it. Setting this true suppresses that built-in
          // redirect so AuthCallback is the single source of truth for post-login
          // routing (it restores originalRoute). Silent renew is unaffected (the
          // library gates that navigation separately on !isRenewProcess).
          triggerAuthorizationResultEvent: true,
          secureRoutes,
        },
      },
      // Run checkAuth() exactly once at app startup (restores the session from
      // storage and processes the /callback response). The guard and callback
      // component then only read isAuthenticated$ — calling checkAuth() on every
      // navigation re-validates the nonce and races silent renew, which produced
      // "incorrect nonce, did you call checkAuth() multiple times?" failures.
      withAppInitializerAuthCheck(),
    ),
  ]);
